import React from 'react';
import { useLocation } from 'react-router-dom';
import SEO from './SEO';
import routeConfig from '../config/seoConfig';
import KNOWN_ROUTES from '../config/knownRoutes';

const DEFAULT_CFG = {
  title: 'SSF Detail | Premium Car Detailing, Ceramic Coating & PPF in Boca Raton, FL',
  description:
    'Award-winning car detailing, Kovalent ceramic coating, STEK paint protection film, and window tinting in Boca Raton, FL. Serving South Florida & Tampa Bay with 7+ years of luxury & exotic car expertise. 150+ 5-star reviews.',
  keywords:
    'car detailing Boca Raton, ceramic coating Boca Raton, paint protection film Boca Raton, PPF installation South Florida, window tint Boca Raton, luxury car detailing, exotic car detailing, mobile detailing Boca Raton, Kovalent ceramic coating, STEK PPF',
};

/**
 * Mounts once inside <BrowserRouter>. Watches the current pathname and
 * renders the matching <SEO> config. Falls back to a default for unconfigured routes.
 *
 * On unmatched routes (any path not in KNOWN_ROUTES), this component returns
 * null so that NotFound.jsx's own <Helmet> is the sole source of
 * title/description/canonical/robots for that URL — required by the Semrush
 * duplicate-meta audit (steps 1.2 + 1.3).
 */
const RouteSEO = () => {
  const { pathname: rawPathname } = useLocation();
  // Static hosts may append a slash to prerendered directory routes. Match
  // React Router's slash-tolerant routing while keeping one canonical URL.
  const pathname = rawPathname.replace(/\/+$/, '') || '/';

  // Skip the hidden admin dashboard — no SEO for that route
  if (pathname.startsWith('/customerinquirylist')) {
    return null;
  }

  /* Landing pages (Google Ads traffic) manage their own <Helmet> — including
     the required noindex meta. Skip RouteSEO so its defaults don't override. */
  if (pathname.startsWith('/lp/')) {
    return null;
  }

  /* Unknown route → this is the wildcard `*` match in App.js, i.e. the
     NotFound page. Skip so NotFound's Helmet emits the ONLY title/canonical/
     robots/prerender-status-code for the URL. */
  if (!KNOWN_ROUTES.has(pathname)) {
    return null;
  }

  const cfg = routeConfig[pathname] || {
    ...DEFAULT_CFG,
    canonical: pathname,
  };

  return (
    <SEO
      title={cfg.title}
      description={cfg.description}
      canonical={cfg.canonical}
      keywords={cfg.keywords}
      ogImage={cfg.ogImage}
      schema={cfg.schema}
    />
  );
};

export default RouteSEO;
